"use client";

import { useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import type { CalendarProps } from "@/components/ui/calendar";
import {
  getCakeDateAvailabilityStateForCart,
  hasBentoCakeSize,
  hasRegularCakeSize,
  type PickupAvailabilityRecord,
} from "@/lib/pickupAvailability";

type PickupDateCalendarProps = Omit<CalendarProps, "selected" | "onSelect" | "fromDate" | "disabled"> & {
  /**
   * Optional: Availability records to automatically compute modifiers and disabled dates.
   * If provided, the component will automatically determine available, limited, and full dates.
   */
  availabilityRecords?: PickupAvailabilityRecord[];
  /**
   * Optional: Order type ("cake", "cake-and-cupcakes", or "cupcakes").
   * Used with availabilityRecords to determine capacity checks.
   */
  orderType?: "cake" | "cake-and-cupcakes" | "cupcakes" | "merch";
  /**
   * Optional: Cake servings (e.g., "1-4" for bento orders).
   * Used with availabilityRecords to determine if this is a bento order.
   */
  cakeServings?: string;
  /**
   * Optional: Allowed serving sizes for the current cake.
   * When provided, date states are derived from all valid sizes for the cake,
   * not just the currently selected serving size.
   */
  cakeAllowedServings?: readonly string[];
  /**
   * Effective bento mapping from the admin order-options: the set of serving
   * VALUES that count toward the BENTO capacity bucket. When provided, the
   * bento/regular split follows this set (custom non-`1-4` bento ranges are
   * judged against the bento counters); when omitted, falls back to the legacy
   * `value === "1-4"` rule. See `hasBentoCakeSize` in lib/pickupAvailability.ts.
   */
  bentoServings?: ReadonlySet<string>;
  /**
   * Cart-aware capacity filter — quantity of bento (`1-4`) cakes needed
   * across the whole cart. Dates whose remaining bento capacity is
   * insufficient get marked `"full"` even when slots remain, so the
   * customer can't pick a date the server will reject. Defaults to 0
   * (no filter; legacy single-cake callers).
   */
  requiredBentoSlots?: number;
  /** Same as `requiredBentoSlots` but for the regular-cake bucket. */
  requiredRegularSlots?: number;
  /**
   * Additive merch pickup-availability constraint (Phase C1,
   * `docs/merch-pickup-availability-plan.md` §4). When non-null, a date is only
   * selectable when ALSO present in this set of allowed ISO dates (the merch
   * per-product intersection for the chosen location). `null`/omitted → no merch
   * constraint (cake-only / cupcake carts byte-identical).
   */
  merchAllowedDates?: ReadonlySet<string> | null;
  /**
   * Optional: Minimum date (today) to check against.
   * Defaults to today's date. Also used to set fromDate automatically.
   */
  minDate?: string; // ISO date string (YYYY-MM-DD)
  /**
   * Selected date as ISO string (YYYY-MM-DD).
   * The component handles conversion to Date internally.
   */
  selectedDate?: string | null;
  /**
   * Callback when a date is selected. Receives ISO string (YYYY-MM-DD) or null.
   * Simplified compared to Calendar's onSelect which receives Date objects.
   */
  onDateSelect?: (isoDate: string | null) => void;
  /**
   * Optional: Custom disabled function to override automatic computation.
   * If not provided, will be computed from availabilityRecords.
   */
  disabled?: (date: Date) => boolean;
  /**
   * Optional: Custom fromDate to override automatic computation from minDate.
   */
  fromDate?: Date;
  /**
   * Optional: Whether to show the legend below the calendar.
   * Defaults to false.
   */
  showLegend?: boolean;
  /**
   * Optional: Custom legend labels. If not provided, uses English defaults.
   */
  legendLabels?: {
    title?: string;
    available?: string;
    limited?: string;
    fullyBooked?: string;
    pastOrNotScheduled?: string;
  };
};

/**
 * Helper function to convert Date to ISO string (YYYY-MM-DD)
 */
function dateToISO(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Helper function to convert ISO string (YYYY-MM-DD) to Date
 */
function dateFromISO(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Reusable calendar component for pickup date selection with standard styling.
 * Includes pink/primary styling for available dates, consistent with the order form.
 * Can automatically compute availability modifiers if availabilityRecords are provided.
 */
export function PickupDateCalendar({
  modifiers,
  modifiersClassNames,
  classNames,
  className,
  availabilityRecords,
  orderType,
  cakeServings,
  cakeAllowedServings,
  bentoServings,
  requiredBentoSlots = 0,
  requiredRegularSlots = 0,
  merchAllowedDates = null,
  minDate,
  selectedDate,
  onDateSelect,
  disabled: customDisabled,
  fromDate: customFromDate,
  showLegend = false,
  legendLabels,
  ...props
}: PickupDateCalendarProps) {
  const today = minDate || dateToISO(new Date());
  const hasCake = orderType === "cake" || orderType === "cake-and-cupcakes";
  // Merch-only carts consume ZERO cake capacity — any OPEN slot is a valid
  // pickup date regardless of the day's cake counters.
  const isMerchOrder = orderType === "merch";
  const isBentoOrder = hasCake && cakeServings === "1-4";
  const hasAllowedServingsContext = Boolean(
    cakeAllowedServings && cakeAllowedServings.length > 0,
  );

  // Helper to find availability record by date
  const findRecordByDate = useMemo(() => {
    if (!availabilityRecords || availabilityRecords.length === 0) {
      return () => undefined;
    }
    return (date: Date): PickupAvailabilityRecord | undefined => {
      const iso = dateToISO(date);
      return availabilityRecords.find((record) => record.date === iso);
    };
  }, [availabilityRecords]);

  // Auto-compute disabled dates if availabilityRecords are provided
  const computedDisabled = useMemo(() => {
    if (customDisabled) return customDisabled; // Allow override

    if (!availabilityRecords || availabilityRecords.length === 0) {
      return (date: Date) => {
        const iso = dateToISO(date);
        return iso < today;
      };
    }

    return (date: Date) => {
      const iso = dateToISO(date);
      const record = findRecordByDate(date);

      if (!record || iso < today) return true;
      if (record.status !== "open") return true;

      // Additive merch constraint (Phase C1): a date the cart's merch products
      // can't fulfil is disabled regardless of the cake calendar. `null` → no
      // constraint (cake-only byte-identical).
      if (merchAllowedDates && !merchAllowedDates.has(iso)) return true;

      // Merch-only: an OPEN slot is always selectable (cake counters irrelevant).
      if (isMerchOrder) return false;

      if (hasCake && hasAllowedServingsContext) {
        const state = getCakeDateAvailabilityStateForCart(record, {
          hasBentoOption: hasBentoCakeSize(cakeAllowedServings, bentoServings),
          hasRegularOption: hasRegularCakeSize(cakeAllowedServings, bentoServings),
          requiredBentoSlots,
          requiredRegularSlots,
        });
        return state.status === "full" || state.status === "closed";
      }

      // Check if fully booked: both counters must be exactly full
      const isRegularFull = record.maxOrders > 0 && record.confirmedOrders === record.maxOrders;
      const isBentoFull = record.maxBentoOrders > 0 && record.confirmedBentoOrders === record.maxBentoOrders;
      return isRegularFull && isBentoFull;
    };
  }, [
    availabilityRecords,
    today,
    findRecordByDate,
    customDisabled,
    hasCake,
    isMerchOrder,
    hasAllowedServingsContext,
    cakeAllowedServings,
    bentoServings,
    requiredBentoSlots,
    requiredRegularSlots,
    merchAllowedDates,
  ]);

  // Auto-set fromDate from minDate
  const computedFromDate = customFromDate || (minDate ? new Date(minDate) : new Date());

  // Convert selectedDate (ISO string) to Date object
  const selectedDateObj = selectedDate ? dateFromISO(selectedDate) : undefined;

  // Handle date selection - convert Date to ISO string for callback
  // Type assertion needed because DayPicker's onSelect has different signatures for single vs range mode
  const handleSelect = ((date: Date | undefined) => {
    if (onDateSelect) {
      onDateSelect(date ? dateToISO(date) : null);
    }
  }) as any;

  // Compute modifiers automatically if availabilityRecords are provided
  const computedModifiers = useMemo(() => {
    if (!availabilityRecords || availabilityRecords.length === 0) {
      return modifiers || {};
    }

    return {
      available: (date: Date) => {
        const iso = dateToISO(date);
        const record = findRecordByDate(date);
        if (!record || record.status !== "open" || iso < today) return false;

        // Additive merch constraint (Phase C1): not in the merch intersection →
        // not selectable, so don't highlight it as available.
        if (merchAllowedDates && !merchAllowedDates.has(iso)) return false;

        // Merch-only: an OPEN slot is available (cake counters irrelevant).
        if (isMerchOrder) return true;

        if (hasCake && hasAllowedServingsContext) {
          const state = getCakeDateAvailabilityStateForCart(record, {
            hasBentoOption: hasBentoCakeSize(cakeAllowedServings, bentoServings),
            hasRegularOption: hasRegularCakeSize(cakeAllowedServings, bentoServings),
            requiredBentoSlots,
            requiredRegularSlots,
          });
          return state.status === "available";
        }

        // If order type is not set or it's cupcakes only, show as available if not full
        if (!hasCake) {
          return record.maxOrders > 0 && record.confirmedOrders < record.maxOrders;
        }

        // Check if it's limited (1 away from full) - if so, don't mark as available (will be marked as limited)
        if (isBentoOrder) {
          const isLimited = record.maxBentoOrders > 0 && record.confirmedBentoOrders === record.maxBentoOrders - 1;
          if (isLimited) return false;
          return record.maxBentoOrders > 0 && record.confirmedBentoOrders < record.maxBentoOrders;
        } else {
          const isLimited = record.maxOrders > 0 && record.confirmedOrders === record.maxOrders - 1;
          if (isLimited) return false;
          return record.maxOrders > 0 && record.confirmedOrders < record.maxOrders;
        }
      },
      limited: (date: Date) => {
        if (!hasCake) return false; // Only show limited for cake orders
        const iso = dateToISO(date);
        const record = findRecordByDate(date);
        if (!record || record.status !== "open" || iso < today) return false;

        // Additive merch constraint (Phase C1): not in the merch intersection →
        // not selectable, so don't highlight it as limited either.
        if (merchAllowedDates && !merchAllowedDates.has(iso)) return false;

        if (hasCake && hasAllowedServingsContext) {
          const state = getCakeDateAvailabilityStateForCart(record, {
            hasBentoOption: hasBentoCakeSize(cakeAllowedServings, bentoServings),
            hasRegularOption: hasRegularCakeSize(cakeAllowedServings, bentoServings),
            requiredBentoSlots,
            requiredRegularSlots,
          });
          return state.status === "limited";
        }

        // Check if 1 order away from max capacity
        if (isBentoOrder) {
          return record.maxBentoOrders > 0 && record.confirmedBentoOrders === record.maxBentoOrders - 1;
        } else {
          return record.maxOrders > 0 && record.confirmedOrders === record.maxOrders - 1;
        }
      },
      full: (date: Date) => {
        const record = findRecordByDate(date);
        if (!record) return false;

        if (hasCake && hasAllowedServingsContext) {
          const state = getCakeDateAvailabilityStateForCart(record, {
            hasBentoOption: hasBentoCakeSize(cakeAllowedServings, bentoServings),
            hasRegularOption: hasRegularCakeSize(cakeAllowedServings, bentoServings),
            requiredBentoSlots,
            requiredRegularSlots,
          });
          return state.status === "full";
        }

        if (record.status === "full") {
          return true;
        }

        // Check if fully booked: both counters must be exactly full
        const isRegularFull = record.maxOrders > 0 && record.confirmedOrders === record.maxOrders;
        const isBentoFull = record.maxBentoOrders > 0 && record.confirmedBentoOrders === record.maxBentoOrders;
        return isRegularFull && isBentoFull;
      },
      ...modifiers, // Allow overriding computed modifiers
    };
  }, [
    availabilityRecords,
    today,
    hasCake,
    isMerchOrder,
    isBentoOrder,
    findRecordByDate,
    modifiers,
    hasAllowedServingsContext,
    cakeAllowedServings,
    bentoServings,
    requiredBentoSlots,
    requiredRegularSlots,
    merchAllowedDates,
  ]);

  // Default modifiers for available dates styling
  const defaultModifiers = computedModifiers;

  // Default modifiersClassNames with pink/primary styling for available dates
  const defaultModifiersClassNames = {
    available: "!border-2 !border-primary/60 !bg-primary/10 !text-primary !font-semibold hover:!border-primary hover:!bg-primary/15 !w-full !h-full !rounded-md",
    limited: "!border-2 !border-orange-500/60 !bg-orange-500/10 !text-orange-700 !font-semibold hover:!border-orange-500 hover:!bg-orange-500/15 !w-full !h-full !rounded-md",
    full: "!bg-gray-300 !text-gray-600 !opacity-80 !outline-none !border-none !cursor-not-allowed hover:!bg-gray-300 hover:!opacity-80 hover:!text-gray-600",
    ...modifiersClassNames,
  };

  // Default classNames for consistent styling
  const defaultClassNames = {
    day_today: "outline outline-1 outline-primary/40 text-primary font-semibold",
    day_disabled: "text-gray-400 opacity-60 bg-gray-50",
    day_selected: "!bg-primary/90 !text-white hover:!bg-primary/80 hover:!text-white font-semibold !border-2 !border-primary !rounded-md shadow-sm",
    day: "hover:bg-muted/60 hover:text-foreground",
    ...classNames,
  };

  // Default className
  const defaultClassName = "w-full rounded-lg border border-border bg-card shadow-sm";

  // Extract mode and other props, ensuring mode is set to "single"
  const { mode = "single", ...restProps } = props;

  // Default legend labels
  const defaultLegendLabels = {
    title: "Legend",
    available: "Available",
    limited: "Limited (1 left)",
    fullyBooked: "Fully booked",
    pastOrNotScheduled: "Unavailable",
    ...legendLabels,
  };

  return (
    <div className="space-y-4">
      <Calendar
        key={selectedDate || "no-selection"}
        mode={mode as "single"}
        {...restProps}
        selected={selectedDateObj}
        defaultMonth={selectedDateObj || computedFromDate}
        onSelect={handleSelect as (date: Date | undefined) => void}
        fromDate={computedFromDate}
        disabled={computedDisabled}
        modifiers={defaultModifiers}
        modifiersClassNames={defaultModifiersClassNames}
        classNames={defaultClassNames}
        className={className || defaultClassName}
      />
      
      {showLegend && (
        <div className="rounded-lg border border-border/50 bg-muted/30 p-2.5 sm:p-3 mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-2.5">
            <div className="flex items-start gap-1.5 sm:gap-2">
              <span className="inline-flex h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 rounded border-2 border-primary/60 bg-primary/10 items-center justify-center mt-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
              </span>
              <span className="text-[10px] sm:text-xs text-muted-foreground font-medium leading-tight flex-1">{defaultLegendLabels.available}</span>
            </div>
            <div className="flex items-start gap-1.5 sm:gap-2">
              <span className="inline-flex h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 rounded border-2 border-orange-500/60 bg-orange-500/10 items-center justify-center mt-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-orange-600"></span>
              </span>
              <span className="text-[10px] sm:text-xs text-muted-foreground font-medium leading-tight flex-1">{defaultLegendLabels.limited}</span>
            </div>
            <div className="flex items-start gap-1.5 sm:gap-2">
              <span className="inline-flex h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 rounded bg-gray-300 items-center justify-center mt-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-gray-600"></span>
              </span>
              <span className="text-[10px] sm:text-xs text-muted-foreground font-medium leading-tight flex-1">{defaultLegendLabels.fullyBooked}</span>
            </div>
            <div className="flex items-start gap-1.5 sm:gap-2">
              <span className="inline-flex h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 rounded bg-gray-100 border border-gray-200 items-center justify-center opacity-60 mt-0.5">
                <span className="h-1 w-1 rounded-full bg-gray-400"></span>
              </span>
              <span className="text-[10px] sm:text-xs text-muted-foreground font-medium leading-tight flex-1">{defaultLegendLabels.pastOrNotScheduled}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
